<template>
  <Modal
    v-model:open="isOpen"
    :btn-to-close="false"
    :content-class="{ 'flex flex-col gap-2 relative': true }"
    :content-padding="true"
    max-height="400px"
    min-height="200px"
    width="410px"
  >
    <template #header>
      <Button v-if="selectedGroup" :variant="ButtonVariant.Tertiary" @click="onBack">
        <template #prefix>
          <Icon src="arrow_left" />
        </template>
        {{ t("back") }}
      </Button>
      <p class="text-body-default-m">{{ computedGroup?.label || t("settings.printservice.title") }}</p>
    </template>
    <Overlay v-if="pending" />
    <template v-else-if="hasPrinters">
      <WizardPrinterButton
        v-if="defaultPrinter && !selectedGroup"
        :title="defaultPrinter.label"
        :subtitle="defaultPrinter.data?.default ? t('default') : ''"
        :alias="defaultPrinter.value"
        icon="printer"
        @click="() => print(defaultPrinter.value)"
      />
      <div v-if="hasGroups && printerGroups.length > 1 && !selectedGroup">
        <WizardPrinterButtonGroup
          v-for="group in printerGroups"
          :key="group.value"
          class="mb-2"
          :group="group"
          @click="selectGroup(group.value)"
        />
      </div>
      <template v-else>
        <InputText v-model="searchQuery" :placeholder="t('search printers')" clearable autocomplete="off">
          <template #prefix>
            <Icon src="bold/magnifying_glass" :size="ComponentSize.default" />
          </template>
        </InputText>
        <WizardPrinterButton
          v-for="printer in computedPrinters"
          :key="printer.data?.id"
          :title="printer.label"
          :subtitle="printer.data?.default ? t('default') : ''"
          :alias="printer.value"
          icon="printer"
          @click="() => print(printer.value)"
        />
      </template>
    </template>
    <div v-else>
      <p class="text-body-default mb-2">{{ t("print.no_printers") }}</p>
      <p class="text-body-default">{{ t("print.to_print") }}</p>
      <div class="mt-8 flex w-full gap-3">
        <Button class="ml-auto" @click="close">{{ t("cancel") }}</Button>
        <Button :variant="ButtonVariant.Primary" to="/settings/printers">{{ t("go_to_settings") }}</Button>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
  import type { IPrinterGroup, PrinterOption } from "~/types/printer";

  // Composables & Services
  const { t } = useI18n();
  const { getPrinters, getPrinterGroups, printLabel, printBulkLabels, printTransfer, printConsignment } = usePrinters();
  const { getOption } = useOptionsStore();

  // Data & State
  const { data: printers, pending, hasPrinters } = await getPrinters();
  const { data: printerGroups, hasGroups } = await getPrinterGroups();
  const searchQuery = ref<string>("");

  // Modal state
  const isOpen = ref(false);
  const selectedGroup = ref<string | null>(null);

  // Print job data
  const packageNumber = ref<string>();
  const transferId = ref<number>();
  const consignmentNumber = ref<string>();
  const selectAll = ref(false);
  const selected = ref<string[] | number[]>([]);
  const filters = ref<{ [key: string]: string | number | boolean }>({});

  // Computed Properties
  const computedPrinters = computed<PrinterOption[]>(() => {
    if (printerGroups.value.length === 1)
      return printers.value.filter((printer: PrinterOption) =>
        printer.label.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    const group = printerGroups.value.find((g: IPrinterGroup) => g.value === selectedGroup.value);
    return group
      ? group.printers.filter((printer: PrinterOption) =>
          printer.label.toLowerCase().includes(searchQuery.value.toLowerCase())
        )
      : [];
  });
  const computedGroup = computed<IPrinterGroup | null>(() => {
    if (!selectedGroup.value) return null;
    return printerGroups.value.find((g: IPrinterGroup) => g.value === selectedGroup.value) || null;
  });

  // Methods
  const open = () => {
    isOpen.value = true;
  };

  const close = () => {
    isOpen.value = false;
    reset();
  };

  const reset = () => {
    packageNumber.value = undefined;
    transferId.value = undefined;
    consignmentNumber.value = undefined;
    selectAll.value = false;
    selected.value = [];
    filters.value = {};
    selectedGroup.value = null;
    searchQuery.value = "";
  };

  const defaultPrinter = computed(() => {
    const defaultPrinterId = getOption("default_label_printer_id");
    if (!defaultPrinterId) return null;
    const defaultPrinterExists = printers.value.some(
      (printer: PrinterOption) => Number(printer.id) === Number(defaultPrinterId)
    );
    if (!defaultPrinterExists) return null;
    return printers.value.find((printer: PrinterOption) => Number(printer.id) === Number(defaultPrinterId)) || null;
  });

  const selectGroup = (groupValue: string) => {
    selectedGroup.value = groupValue;
  };

  const print = (printer: string) => {
    if (packageNumber.value) {
      printLabel(printer, packageNumber.value);
    } else if (transferId.value) {
      printTransfer(printer, transferId.value);
    } else if (consignmentNumber.value) {
      printConsignment(printer, consignmentNumber.value);
    } else {
      printBulkLabels(printer, selectAll.value, selected.value, filters.value);
    }
  };

  const onBack = () => {
    selectedGroup.value = null;
    searchQuery.value = "";
  };

  // Event Bus Handlers
  const handleAddLabel = (
    package_number?: string,
    select_all?: boolean,
    select?: string[] | number[],
    filter?: { [key: string]: string | number | boolean }
  ) => {
    reset();
    packageNumber.value = package_number;
    selectAll.value = select_all || false;
    selected.value = select || [];
    filters.value = filter || {};
  };

  const handleAddTransfer = (transfer_id: number) => {
    reset();
    transferId.value = transfer_id;
  };

  const handleAddConsignment = (consignment_number: string) => {
    reset();
    consignmentNumber.value = consignment_number;
  };

  // Lifecycle Hooks
  onMounted(() => {
    EventBus.$on("PRINTING_ADD_LABEL", handleAddLabel);
    EventBus.$on("PRINTING_ADD_TRANSFER", handleAddTransfer);
    EventBus.$on("PRINTING_ADD_CONSIGNMENT", handleAddConsignment);
    EventBus.$on("PRINTING_OPEN", open);
    EventBus.$on("PRINTING_CLOSE", close);
  });

  onBeforeUnmount(() => {
    EventBus.$off("PRINTING_ADD_LABEL", handleAddLabel);
    EventBus.$off("PRINTING_ADD_TRANSFER", handleAddTransfer);
    EventBus.$off("PRINTING_ADD_CONSIGNMENT", handleAddConsignment);
    EventBus.$off("PRINTING_OPEN", open);
    EventBus.$off("PRINTING_CLOSE", close);
  });
</script>
